"use client";

import { useTranslations } from "next-intl";
import {
	ConstantAutocompleteField,
	RadioCardField,
	SelectField,
} from "@/components/form";
import { useCountryConfig } from "@/config/country-context";
import {
	SWEDISH_ACCOUNT_DIGITS,
	SWEDISH_CLEARING_DIGITS,
	SWEDISH_FULL_ACCOUNT_DIGITS,
} from "@/features/customer-information/utils/swedish-account-fields";
import { DUE_DATE_OPTIONS } from "@/features/customer-information/utils/trusted-account";
import { AccountNumberField } from "./account-number-field";
import { DigitsInputField } from "./digits-input-field";
import { NumberOfCarsField } from "./number-of-cars-field";

type SelectOption = { value: string; label: string };

interface CustomerKycFieldsProps {
	accountNumbers: string[];
	prefilledAccountNumber: string;
	isCoapplicant: boolean;
	hasMultipleTrustedAccounts: boolean;
	/** SE: a whole clearing + account number is prefilled in one field. */
	isWholeAccountNumber: boolean;
	showLoanPurposeSelect: boolean;
	loanPurposeOptions: SelectOption[];
	askNumberOfCars: boolean;
	pepOptions: { value: boolean; label: string }[];
}

/**
 * Shared "know your customer" field group rendered on the loan and combined
 * customer-information screens: citizenship, account/clearing inputs, loan
 * purpose, the car count when FLOW has none, PEP and, where the country asks
 * for it, the payout due date. Both screens use the
 * `pages.CustomerInformationPage` namespace, so labels are resolved here.
 */
export const CustomerKycFields = ({
	accountNumbers,
	prefilledAccountNumber,
	isCoapplicant,
	hasMultipleTrustedAccounts,
	isWholeAccountNumber,
	showLoanPurposeSelect,
	loanPurposeOptions,
	askNumberOfCars,
	pepOptions,
}: CustomerKycFieldsProps) => {
	const t = useTranslations("pages.CustomerInformationPage");
	const { rules } = useCountryConfig();
	const hasSwedishAccount = !isCoapplicant && rules.payoutAccount === "swedish";
	const hasFinnishAccount =
		!isCoapplicant && rules.payoutAccount === "finnish-iban";
	// Mutually exclusive: pick from the trusted accounts, correct the one whole
	// number we were given, or type clearing and account separately.
	const showWholeAccountField =
		!hasMultipleTrustedAccounts && isWholeAccountNumber;
	const showSplitAccountFields =
		!hasMultipleTrustedAccounts && !isWholeAccountNumber;

	return (
		<>
			{rules.fixedCitizenship === null && (
				<ConstantAutocompleteField
					name="citizenship"
					label={t("selectInput.labels.citizenship")}
					optionsKey="countries"
					max={3}
				/>
			)}

			{hasSwedishAccount && hasMultipleTrustedAccounts && (
				<AccountNumberField
					name="accountNumber"
					label={t("inputGroup.accountNumber")}
					accountNumbers={accountNumbers}
					selectLabel={t("selectInput.labels.accountNumber")}
				/>
			)}

			{hasSwedishAccount && showWholeAccountField && (
				<AccountNumberField
					name="accountNumber"
					label={t("inputGroup.clearingAndAccountNumber")}
					digitsOnly
					maxDigits={SWEDISH_FULL_ACCOUNT_DIGITS.max}
				/>
			)}

			{hasSwedishAccount && showSplitAccountFields && (
				<>
					<DigitsInputField
						name="clearingNumber"
						label={t("inputGroup.clearing")}
						maxDigits={SWEDISH_CLEARING_DIGITS.max}
					/>
					<AccountNumberField
						name="accountNumber"
						label={t("inputGroup.accountNumber")}
						digitsOnly
						maxDigits={SWEDISH_ACCOUNT_DIGITS.max}
					/>
				</>
			)}

			{hasFinnishAccount && (
				<AccountNumberField
					name="accountNumber"
					label={t("inputGroup.accountNumber")}
					readOnly={Boolean(prefilledAccountNumber)}
				/>
			)}

			{showLoanPurposeSelect && (
				<SelectField
					name="loanPurpose"
					label={t("loanPurpose.label")}
					options={loanPurposeOptions}
				/>
			)}

			{askNumberOfCars && <NumberOfCarsField />}

			<RadioCardField
				name="isPEP"
				label={t("binaryResponse.label")}
				tooltip={t("tooltips.pep.infoText")}
				options={pepOptions}
				className="pt-1"
			/>

			{rules.dueDateChoice && !isCoapplicant && (
				<RadioCardField
					name="dueDate"
					label={t("dueDate.label")}
					options={DUE_DATE_OPTIONS.map((value) => ({
						value,
						label: value,
					}))}
					columns={3}
					className="pt-1"
				/>
			)}
		</>
	);
};
